// lib/cascade/grids.js
// ═══════════════════════════════════════════════════════════
// INTERVENTION GRIDS — Korea 9×4 + Atlantic Cod 5×4
//
// Source-of-truth:
//   docs/CONTINUUM_PHASE_2_KOREA_COD.md §IV (Korea, 36 cells)
//   docs/CONTINUUM_PHASE_2_KOREA_COD.md §V (cod, 20 cells)
//
// Grid model: spirals (rows) × intervention layers (columns).
// Each cell carries an engagement level + short note on what
// has been tried at that intersection.
//
// Engagement levels:
//   strong   — sustained ≥1σ above baseline ≥5 years
//   partial  — some activation but not sustained / below threshold
//   none     — no measurable activation
//   tbd      — evidence gap, future session work
// ═══════════════════════════════════════════════════════════

export const ENGAGEMENT_LEVELS = {
  strong:  { label: 'STRONG',  colorKey: 'sage',  short: 'Sustained ≥1σ above baseline ≥5 years' },
  partial: { label: 'PARTIAL', colorKey: 'gold',  short: 'Some activation but not sustained / below threshold' },
  none:    { label: 'NONE',    colorKey: 'dim',   short: 'No measurable activation' },
  tbd:     { label: 'TBD',     colorKey: 'amber', short: 'Evidence gap, future session' },
};

// ─── Korea — 9 spirals × 4 layers (Personal, Corporate, Policy, Cultural)
export const KOREA_GRID = {
  caseId: 'korea',
  caseLabel: 'Korea',
  substrate: 'human',
  caseColorKey: 'gold',
  // Canonical layer-engagement reading per docs/CONTINUUM_PHASE_2_KOREA_COD.md §IV
  // ("Korea aggregate: 1 strong-engagement layer (Policy)").
  layerEngagement: {
    personal: 'partial',
    corporate: 'partial',
    policy: 'strong',
    cultural: 'partial',
  },
  layers: [
    { id: 'personal',  label: 'Personal',  short: 'Individual choices, household-level strategy' },
    { id: 'corporate', label: 'Corporate', short: 'Business model adaptation, organizational response' },
    { id: 'policy',    label: 'Policy',    short: 'Government interventions, fiscal/regulatory' },
    { id: 'cultural',  label: 'Cultural',  short: 'Narrative shift, values change, social patterns' },
  ],
  spirals: [
    { id: 'S1', label: 'Pension fiscal',           short: 'Boomer retirement obligations vs tax revenue' },
    { id: 'S2', label: 'Regional real estate',     short: 'Vacancy spirals + tax base collapse' },
    { id: 'S3', label: 'Brain drain',              short: 'Educated young emigration accelerating' },
    { id: 'S4', label: 'Generational political war', short: 'Working-age tax burden vs elderly voter dominance' },
    { id: 'S5', label: 'Defense conscription',     short: 'Conscription pool vs standing-force requirement (Korea-specific)' },
    { id: 'S6', label: 'Loneliness / mental health', short: 'Single-elderly threshold, suicide rates, social fabric' },
    { id: 'S7', label: 'Cultural anti-natalism',   short: '4B + family-formation collapse' },
    { id: 'S8', label: 'Sovereign debt',           short: 'Demographic + fiscal + currency convergence' },
    { id: 'S9', label: 'Innovation collapse',      short: 'Startup formation rate vs replacement' },
  ],
  cells: {
    'S1.personal':  { level: 'partial', note: 'late-career work norm growing' },
    'S1.corporate': { level: 'partial', note: 'corporate retirement programs' },
    'S1.policy':    { level: 'strong',  note: 'pension age raises 60→65→68; means-testing' },
    'S1.cultural':  { level: 'none' },

    'S2.personal':  { level: 'partial', note: 'internal migration to Seoul ongoing' },
    'S2.corporate': { level: 'partial', note: 'corporate flight to Seoul' },
    'S2.policy':    { level: 'partial', note: 'regional revitalization budgets, Sejong administrative capital' },
    'S2.cultural':  { level: 'none',    note: 'no anti-Seoul-concentration cultural narrative' },

    'S3.personal':  { level: 'partial', note: 'youth emigration to US/Japan/Australia' },
    'S3.corporate': { level: 'none',    note: 'limited reverse-migration recruiting' },
    'S3.policy':    { level: 'none',    note: 'no Aliyah-equivalent program' },
    'S3.cultural':  { level: 'none',    note: 'no "come home and build" narrative' },

    'S4.personal':  { level: 'partial', note: 'youth political mobilization' },
    'S4.corporate': { level: 'none' },
    'S4.policy':    { level: 'none',    note: 'no generational compact framework' },
    'S4.cultural':  { level: 'partial', note: '4B is youth identity formation but in separationist direction' },

    'S5.personal':  { level: 'none',    note: 'emigration to avoid service growing' },
    'S5.corporate': { level: 'partial', note: 'defense industry automation pivot (drones, AI)' },
    'S5.policy':    { level: 'strong',  note: 'conscription extensions, women considered' },
    'S5.cultural':  { level: 'partial', note: 'military-service-as-civic-duty narrative weakening' },

    'S6.personal':  { level: 'tbd' },
    'S6.corporate': { level: 'tbd' },
    'S6.policy':    { level: 'partial', note: 'mental health budget growth post-2018' },
    'S6.cultural':  { level: 'none',    note: 'no Loneliness Minister equivalent (cf. Japan, UK)' },

    'S7.personal':  { level: 'none',    note: 'opposite direction (4B participation)' },
    'S7.corporate': { level: 'none' },
    'S7.policy':    { level: 'strong',  note: '₩500T (cumulative 2006-2024) — but exclusively Layer 3' },
    'S7.cultural':  { level: 'none',    note: 'H-Continuum-11 critical evidence: Cultural layer absent' },

    'S8.personal':  { level: 'partial', note: 'household savings discipline cultural' },
    'S8.corporate': { level: 'partial', note: 'chaebol balance-sheet discipline' },
    'S8.policy':    { level: 'partial', note: 'fiscal conservatism vs natalism spending tension' },
    'S8.cultural':  { level: 'none' },

    'S9.personal':  { level: 'partial', note: 'startup activity (Naver, Kakao, Coupang)' },
    'S9.corporate': { level: 'strong',  note: 'corporate venture, intrapreneurship' },
    'S9.policy':    { level: 'partial', note: 'startup visa, sandbox regulation' },
    'S9.cultural':  { level: 'partial', note: 'startup-nation narrative present but weaker than Israel\'s' },
  },
};

// ─── Atlantic cod — 5 spirals × 4 sublayers (Individual, Industry, Policy, Cultural)
export const COD_GRID = {
  caseId: 'cod',
  caseLabel: 'Atlantic cod (NW)',
  substrate: 'eco',
  caseColorKey: 'teal',
  // Canonical sublayer-engagement reading per docs/CONTINUUM_PHASE_2_KOREA_COD.md §V
  // ("Cod aggregate: 2 strong-engagement sublayers (Policy + Industry partial-strong)").
  layerEngagement: {
    individual: 'partial',
    industry: 'strong',
    policy: 'strong',
    cultural: 'partial',
  },
  layers: [
    { id: 'individual', label: 'Individual', short: 'Per-capita conservation behaviors in relevant geography' },
    { id: 'industry',   label: 'Industry',   short: '% of relevant industry adopting voluntary protective practices' },
    { id: 'policy',     label: 'Policy',     short: 'Legislative protection + protected-area coverage + funding' },
    { id: 'cultural',   label: 'Cultural',   short: 'Species-as-kin / species-as-resource framing in popular media' },
  ],
  spirals: [
    { id: 'E1', label: 'Overfishing pressure',                      short: 'Direct extraction past Allee threshold' },
    { id: 'E2', label: 'Regime shift / hysteresis',                 short: 'Crustacean-dominated alternative stable state lock-in' },
    { id: 'E3', label: 'Climate stress',                            short: 'Warming + productivity shift + thermal niche shift' },
    { id: 'E4', label: 'Juvenile mortality / Allee',                short: 'Spawning aggregation collapse + recruitment failure' },
    { id: 'E5', label: 'Cultural-economic dependency (Newfoundland)', short: 'Identity tied to cod fishery; recovery politics tangled' },
  ],
  cells: {
    'E1.individual': { level: 'partial', note: 'recreational fishing self-restraint' },
    'E1.industry':   { level: 'strong',  note: 'license buyouts + vessel decommissioning' },
    'E1.policy':     { level: 'strong',  note: 'moratorium, gear restrictions, quota system' },
    'E1.cultural':   { level: 'partial', note: 'sustainable-seafood consumer movement' },

    'E2.individual': { level: 'none' },
    'E2.industry':   { level: 'partial', note: 'crustacean-fishery pivot (economically rewarded)' },
    'E2.policy':     { level: 'partial', note: 'ecosystem-based management (uneven adoption)' },
    'E2.cultural':   { level: 'none',    note: 'no narrative for regime-reversal effort' },

    'E3.individual': { level: 'none',    note: 'individual cannot act on climate at species level' },
    'E3.industry':   { level: 'none',    note: 'fishery industry climate mitigation absent' },
    'E3.policy':     { level: 'strong',  note: 'general policy level (climate policy) but weak species-targeted' },
    'E3.cultural':   { level: 'partial', note: 'climate-cod connection in conservation media but weak' },

    'E4.individual': { level: 'none' },
    'E4.industry':   { level: 'partial', note: 'bycatch reduction' },
    'E4.policy':     { level: 'partial', note: 'protected nursery areas (limited)' },
    'E4.cultural':   { level: 'none' },

    'E5.individual': { level: 'strong',  note: 'Newfoundland identity preservation' },
    'E5.industry':   { level: 'partial', note: 'community-managed fisheries pilot' },
    'E5.policy':     { level: 'partial', note: 'Indigenous co-management funding' },
    'E5.cultural':   { level: 'partial', note: 'cod-as-Newfoundland-icon present but conservation-frame dominant' },
  },
};

export const GRIDS = [KOREA_GRID, COD_GRID];

// Compute summary statistics for a grid.
// - cell-level counts per layer (strong/partial/none/tbd) computed from cells
// - layer-engagement classification taken from grid.layerEngagement (canonical per docs)
//   rather than auto-computed, because the per-layer "strong-engaged" determination
//   is a methodology call (sustained ≥1σ above baseline ≥5 years per H-10) that
//   the docs make explicitly, not a mechanical count over cells.
export function gridSummary(grid) {
  const counts = {};
  for (const layer of grid.layers) {
    counts[layer.id] = { strong: 0, partial: 0, none: 0, tbd: 0 };
  }
  for (const spiral of grid.spirals) {
    for (const layer of grid.layers) {
      const cell = grid.cells[`${spiral.id}.${layer.id}`];
      const level = cell?.level || 'none';
      counts[layer.id][level] = (counts[layer.id][level] || 0) + 1;
    }
  }
  // Canonical layer-engagement reading from the grid (docs are source-of-truth)
  const layerEngagement = grid.layerEngagement || {};
  const strongCount = Object.values(layerEngagement).filter((v) => v === 'strong').length;
  const partialCount = Object.values(layerEngagement).filter((v) => v === 'partial').length;
  const noneCount = Object.values(layerEngagement).filter((v) => v === 'none').length;
  return { counts, layerEngagement, strongCount, partialCount, noneCount };
}
